var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { IdentifiedPatientState } from "healthex-common/consent.dtos";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { PatientConsentFlow } from "./PatientConsentUIManager";
import { MobileBottomNavigationButtonType, PatientStudiesListViewTab, } from "./PatientResearchDashboardUIManager";
import { getObservableT } from "../Infra/i18next";
import { getTranslatedStudyText } from "./TranslationService";
import { getPatientDateString, getPatientRequestText, getPatientStatusChip } from "../patient.utils";
import { ProjectType } from "healthex-common/study.dtos";
const t = getObservableT("patients");
export class PatientStudiesListViewModel {
    constructor(container) {
        this.studyDetailsDialogOpen = false;
        this.studyDetailsStudyId = null;
        this.studyDetailsShowInterested = false;
        this.patientResearchDashboardUIManager = container.patientResearchDashboardUIManager;
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.patientPreferencesManager = container.patientPreferencesManager;
        this.browserLanguageUIManager = container.browserLanguageUIManager;
        this.authManager = container.authManager;
    }
    get currentTab() {
        return this.patientResearchDashboardUIManager.currentTab;
    }
    get tabsToShow() {
        return [
            { value: PatientStudiesListViewTab.ALL, label: t("All requests") },
            { value: PatientStudiesListViewTab.ACTION_NEEDED, label: t("Action needed") },
            { value: PatientStudiesListViewTab.AGREED, label: t("Agreed") },
            { value: PatientStudiesListViewTab.DECLINED, label: t("Declined") },
        ];
    }
    get isOptedOut() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return NOT_LOADED;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.optedOut) !== null && _b !== void 0 ? _b : false;
    }
    get noConsentsAvailable() {
        return this.patientResearchDashboardUIManager.noConsentsAvailable;
    }
    get showTabs() {
        return this.noConsentsAvailable !== true && this.currentTab !== null;
    }
    get zeroState() {
        var _a;
        if (this.isOptedOut === NOT_LOADED || this.noConsentsAvailable === NOT_LOADED)
            return null;
        if (this.isOptedOut) {
            return {
                title: t("You're not receiving requests right now"),
                text: t("Your health records can help {{ organizationName }} deliver data-driven, proactive care that supports your health journey\n\nYou’ve opted out of HealthEx, but you can update this anytime in your settings.", { organizationName: this.authManager.orgName }),
                buttonText: t("Join HealthEx again"),
                buttonOnClick: () => this.optInAgain(),
            };
        }
        if (this.noConsentsAvailable) {
            return {
                title: t("You don’t have any requests"),
                text: t("When {{ organizationName }} requests access to your health records, you’ll see it here.\n\nYou can review the request and decide what feels right for you.", { organizationName: this.authManager.orgName }),
            };
        }
        else if (this.studies !== NOT_LOADED && ((_a = this.studies) === null || _a === void 0 ? void 0 : _a.length) === 0) {
            return {
                title: t("No requests match your filter"),
                text: t("Looks like you haven’t responded to any requests that way yet. You can change your filter to view more."),
                buttonText: t("Show all requests"),
                buttonOnClick: () => this.patientResearchDashboardUIManager.goToPage(MobileBottomNavigationButtonType.RESEARCH, PatientStudiesListViewTab.ALL),
                showNoRequestsIcon: true,
            };
        }
        return null;
    }
    get studies() {
        return this.patientResearchDashboardUIManager.sortedStudies;
    }
    setCurrentTab(tab) {
        this.patientResearchDashboardUIManager.goToPage(MobileBottomNavigationButtonType.RESEARCH, tab);
    }
    showStudyDetailsDialog() {
        this.studyDetailsDialogOpen = true;
    }
    hideStudyDetailsDialog() {
        this.studyDetailsDialogOpen = false;
        this.studyDetailsStudyId = null;
        this.studyDetailsShowInterested = false;
    }
    async optInAgain() {
        await this.patientPreferencesManager.updatePatientPreferences({ optedOut: false });
    }
    getStudyPatientFriendlyTitle(study) {
        const translatedTitle = getTranslatedStudyText(study, "patientFriendlyTitle", this.browserLanguageUIManager.currentLanguage);
        return translatedTitle || study.patientFriendlyTitle || study.title || "";
    }
    getListItemInfo(study, consent) {
        var _a, _b;
        const actionNeeded = !!consent.pending;
        const agreedUpdateRecordsNeeded = !!consent.updateRecordsNeeded;
        const highlightActionNeeded = actionNeeded || agreedUpdateRecordsNeeded;
        const isInterested = consent.state === IdentifiedPatientState.ENROLLING_INTERESTED || consent.state === IdentifiedPatientState.ENROLLING_CONTACTED;
        const orgName = study.projectType === ProjectType.AI_DATA_USE
            ? (_a = study.aiToolName) !== null && _a !== void 0 ? _a : study.addedByOrganization.name
            : study.addedByOrganization.name;
        const orgLogo = (_b = study.organizationIconUrl) !== null && _b !== void 0 ? _b : null;
        return {
            chip: getPatientStatusChip(consent),
            title: this.getStudyPatientFriendlyTitle(study),
            requestText: getPatientRequestText(study.projectType),
            date: getPatientDateString(consent.timestamp),
            showActionNeededBadge: highlightActionNeeded,
            useBoldText: highlightActionNeeded,
            orgName,
            orgLogo,
            cta: actionNeeded ? () => this.startConsentFlow(study.id) : () => this.openPatientStudyDetailsPage(study.id, isInterested),
        };
    }
    startConsentFlow(studyId) {
        this.patientConsentUIManager.startConsentFlow(studyId, PatientConsentFlow.ENROLLMENT, null, null);
    }
    openPatientStudyDetailsPage(studyId, isInterested) {
        this.studyDetailsStudyId = studyId;
        this.studyDetailsShowInterested = isInterested;
        this.showStudyDetailsDialog();
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientStudiesListViewModel.prototype, "studyDetailsDialogOpen", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientStudiesListViewModel.prototype, "studyDetailsStudyId", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], PatientStudiesListViewModel.prototype, "studyDetailsShowInterested", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientStudiesListViewModel.prototype, "currentTab", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientStudiesListViewModel.prototype, "tabsToShow", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientStudiesListViewModel.prototype, "isOptedOut", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientStudiesListViewModel.prototype, "noConsentsAvailable", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientStudiesListViewModel.prototype, "showTabs", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientStudiesListViewModel.prototype, "zeroState", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientStudiesListViewModel.prototype, "studies", null);
